import { createSlice } from '@reduxjs/toolkit'

import { logout } from '~/auth/slices/auth.slice'
import appConfig from '~/config/app'
import { RootState } from '~/redux/store'
import { UserRole } from '~/types/user-info-types'

interface OracleAccessState {
  /** The safety gate paused this student's Oracle. Persisted, so a reload or a new tab keeps the door closed. */
  isPaused: boolean
}

const initialState: OracleAccessState = { isPaused: false }

const slice = createSlice({
  name: 'oracleAccess',
  initialState,
  reducers: {
    pauseOracle: () => ({ isPaused: true }),
    // The backend is the source of truth: any turn it lets through means the pause was lifted.
    resumeOracle: () => initialState,
  },
  // The next person on the device (school sessionReset runs this case too) starts unpaused.
  extraReducers: (builder) => builder.addCase(logout, () => initialState),
})

const KNOWN_ROLES: string[] = Object.values(UserRole)

/**
 * Whether this user's Oracle turns go through the school safety gate. Product decision: school students are
 * screened and can be paused; school teachers and other staff are not. On the school platform a missing or
 * unrecognised role fails safe to the gate, never to the ungated route. Pure, so the table is assertable.
 */
export const isSafetyGatedUser = (isSchoolPlatform: boolean, role?: string | null): boolean =>
  isSchoolPlatform && (role === UserRole.SCHOOL_STUDENT || !role || !KNOWN_ROLES.includes(role))

export const selectIsSafetyGated = (state: RootState): boolean =>
  isSafetyGatedUser(appConfig.isSchoolPlatform, state.auth.jwtPayload?.role)

/** Only a gated user is ever paused: a pause a student left in this browser cannot lock a teacher's chat. */
export const selectIsOraclePaused = (state: RootState): boolean =>
  state.oracleAccess.isPaused && selectIsSafetyGated(state)

export const { pauseOracle: pauseOracleActionCreator, resumeOracle: resumeOracleActionCreator } = slice.actions

export default slice.reducer
